import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faCheck,
  faCircleNotch,
  faCubes,
  faEllipsisVertical,
  faFolderOpen,
  faTrash,
  faUpRightFromSquare,
  faWandMagicSparkles,
} from "@fortawesome/free-solid-svg-icons";
import { deleteLibraryLesson, patchTeacherLesson } from "../../auth/api-client";
import { useAuth } from "../../auth/AuthContext";
import { PlaygroundPanelSplitter } from "../../playground/PlaygroundPanelSplitter";
import { LessonAdvancedModePromptModal } from "../LessonAdvancedModePromptModal";
import { FloatingActionsMenu } from "../FloatingActionsMenu";
import { WhatsChangedModal } from "../WhatsChangedModal";
import { lessonPreviewAppearanceQuery, teacherPreviewLessonUrl } from "../teacherPreviewLessonUrl";
import {
  applyAiMentionsToDoc,
  emptyMentionDoc,
  isMentionDocEmpty,
  mentionDocFromPlainText,
  toAiPromptText,
  toLessonAiStandardsRequest,
  type MentionPromptDoc,
} from "../standards-mentions/mentionTypes";
import { useDefaultStandardSetId } from "../standards-mentions/useDefaultStandardSetId";
import { useStandardsCatalog } from "../standards-mentions/useStandardsCatalog";
import { useLessonDraft } from "./useLessonDraft";
import { useLessonStandardsSelection } from "./useLessonStandardsSelection";
import { useWorkspaceSplit } from "./useWorkspaceSplit";
import { LessonDetailsSection } from "./LessonDetailsSection";
import { LessonCodeSection } from "./LessonCodeSection";
import { LessonInstructionsSection } from "./LessonInstructionsSection";
import { LessonChecksSection } from "./LessonChecksSection";
import { LessonStandardsSection } from "./LessonStandardsSection";
import { LessonAiAssistPanel } from "./LessonAiAssistPanel";
import { LessonPreviewPane } from "./LessonPreviewPane";
import { useToast } from "../../ui/useToast";

type AuthorTab = "details" | "code" | "instructions" | "checks" | "standards";

export default function LessonWorkspacePage() {
  const { lessonId: existingLessonId } = useParams<{ lessonId?: string }>();
  const navigate = useNavigate();
  const { getBearerToken } = useAuth();
  const bearer = getBearerToken();

  const [searchParams] = useSearchParams();
  const courseIdFromUrl = searchParams.get("courseId") ?? undefined;
  const urlStandardSetId = searchParams.get("standardSetId") ?? undefined;
  const urlStandardId = searchParams.get("standardId") ?? undefined;

  const { setId: defaultSetId } = useDefaultStandardSetId(bearer, {
    courseId: courseIdFromUrl,
    urlStandardSetId,
  });
  const [standardSetId, setStandardSetId] = useState("");
  const [mentionDoc, setMentionDoc] = useState<MentionPromptDoc>(emptyMentionDoc);
  const catalog = useStandardsCatalog(bearer, standardSetId || defaultSetId);

  useEffect(() => {
    if (defaultSetId && !standardSetId) setStandardSetId(defaultSetId);
  }, [defaultSetId, standardSetId]);

  const standards = useLessonStandardsSelection(existingLessonId ?? null, {
    standardSetId: urlStandardSetId,
    standardId: urlStandardId,
  });

  const activeSetId = standardSetId || defaultSetId;
  const promoteToLibraryRef = useRef<() => void>(() => {});
  const { showToast, Toast } = useToast();
  const showDraftSavedToast = useCallback(() => {
    showToast("Draft saved. It won't appear in your library until you move it out of drafts.", {
      action: {
        label: "Move to library",
        onClick: () => promoteToLibraryRef.current(),
      },
    });
  }, [showToast]);

  const ws = useLessonDraft(existingLessonId, {
    afterSave: (id) => standards.persist(id),
    getAiPrompt: () => toAiPromptText(mentionDoc),
    isAiPromptEmpty: () => isMentionDocEmpty(mentionDoc),
    getStandardsRequest: () => toLessonAiStandardsRequest(mentionDoc, activeSetId || undefined),
    onAiAlignment: (codes, terms) => {
      setMentionDoc((doc) =>
        applyAiMentionsToDoc(doc, catalog.standards, catalog.glossary, codes, terms)
      );
      standards.applyAiAlignment(codes, terms);
    },
    onGenerateClearPrompt: () => setMentionDoc(emptyMentionDoc()),
    onDraftSaved: showDraftSavedToast,
  });
  const {
    draft,
    setDraft,
    updateStep,
    updateTest,
    isEvolvingExisting,
    savedLessonId,
    creationPrompt,
    hasUnsavedChanges,
    savedBaseline,
    loadingLesson,
    aiBusy,
    aiProgressMode,
    aiProgress,
    saving,
    error,
    saveSuccess,
    upgradeCta,
    advancedPrompt,
    advancedPromptBusy,
    communityPublishStatus,
    isDraft,
    setIsDraft,
    canMarkAsDraft,
    setError,
    handleGenerate,
    handleRefine,
    handleSave,
    handleAcceptAdvancedMode,
    handlePreferSimpleMode,
    guardedNavigate,
  } = ws;

  const [changelogOpen, setChangelogOpen] = useState(false);
  const isPublished =
    communityPublishStatus === "approved" || communityPublishStatus === "pending_review";

  function onSaveClick() {
    if (isPublished && savedLessonId) {
      setChangelogOpen(true);
      return;
    }
    void handleSave();
  }

  const [activeTab, setActiveTab] = useState<AuthorTab>("details");
  const [aiOpen, setAiOpen] = useState(false);
  const [startedBlank, setStartedBlank] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [togglingDraft, setTogglingDraft] = useState(false);

  const splitRef = useRef<HTMLDivElement | null>(null);
  const menuTriggerRef = useRef<HTMLButtonElement | null>(null);
  const split = useWorkspaceSplit(splitRef);

  const hasContent = isEvolvingExisting || Boolean(savedLessonId) || draft.title.trim().length > 0;
  const showEditor = hasContent || startedBlank;
  const isAdvanced = (draft.workspaceMode ?? "simple") === "advanced";

  useEffect(() => {
    const seededPrompt = searchParams.get("prompt");
    if (seededPrompt) {
      setMentionDoc(mentionDocFromPlainText(seededPrompt));
      setAiOpen(true);
    }
    // Seed once from the deep link (e.g. course standards gap-fill).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!urlStandardId || !catalog.standards.length) return;
    const std = catalog.standards.find((s) => s.id === urlStandardId);
    if (!std) return;
    setMentionDoc((doc) => {
      const has = doc.segments.some(
        (s) => s.type === "mention" && s.token.kind === "standard" && s.token.id === std.id
      );
      if (has) return doc;
      return {
        segments: [
          ...doc.segments,
          {
            type: "mention",
            token: {
              kind: "standard",
              id: std.id,
              label: std.code,
              description: std.description,
            },
          },
          { type: "text", value: " " },
        ],
      };
    });
  }, [urlStandardId, catalog.standards]);

  async function handleDelete() {
    const bearer = getBearerToken();
    if (!bearer || !savedLessonId || deleting) return;
    if (!window.confirm("Delete this lesson? It will move to Deleted lessons and can be restored.")) {
      return;
    }
    setDeleting(true);
    try {
      await deleteLibraryLesson(bearer, savedLessonId);
      navigate("/teacher/lessons");
    } catch {
      setDeleting(false);
    }
  }

  async function handlePromoteToLibrary() {
    const bearer = getBearerToken();
    if (!bearer || !savedLessonId || togglingDraft || isPublished || !isDraft) return;
    setTogglingDraft(true);
    try {
      await patchTeacherLesson(bearer, undefined, savedLessonId, { isDraft: false });
      setIsDraft(false);
      setMenuOpen(false);
      showToast("Lesson moved to your library.");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not move lesson to library");
    } finally {
      setTogglingDraft(false);
    }
  }

  promoteToLibraryRef.current = () => {
    void handlePromoteToLibrary();
  };

  async function handleMoveToDrafts() {
    const bearer = getBearerToken();
    if (!bearer || !savedLessonId || togglingDraft || isPublished || !canMarkAsDraft) return;
    setTogglingDraft(true);
    try {
      await patchTeacherLesson(bearer, undefined, savedLessonId, { isDraft: true });
      setIsDraft(true);
      setMenuOpen(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not update draft status");
    } finally {
      setTogglingDraft(false);
    }
  }

  async function handleToggleDraft() {
    if (isDraft) {
      await handlePromoteToLibrary();
      return;
    }
    await handleMoveToDrafts();
  }

  const fullPreviewUrl =
    savedLessonId && draft.title.trim()
      ? teacherPreviewLessonUrl({
          lessonId: savedLessonId,
          lessonTitle: draft.title,
          ...lessonPreviewAppearanceQuery(draft),
        })
      : null;

  const saveLabel = saving
    ? "Saving…"
    : savedLessonId
      ? isDraft
        ? "Save draft"
        : "Save changes"
      : "Save to library";

  if (loadingLesson) {
    return (
      <div className="lesson-ws-loading">
        <FontAwesomeIcon icon={faCircleNotch} spin /> Loading lesson…
      </div>
    );
  }

  const statusBadge = saving ? (
    <span className="lesson-ws-status lesson-ws-status--saving">
      <FontAwesomeIcon icon={faCircleNotch} spin /> Saving
    </span>
  ) : hasUnsavedChanges ? (
    <span className="lesson-ws-status lesson-ws-status--unsaved">Unsaved changes</span>
  ) : savedLessonId ? (
    <span className="lesson-ws-status lesson-ws-status--saved">
      <FontAwesomeIcon icon={faCheck} /> All changes saved
    </span>
  ) : null;

  const topBar = (
    <header className="lesson-ws-topbar">
      <div className="lesson-ws-topbar-left">
        <button
          type="button"
          className="lesson-ws-back"
          onClick={() => guardedNavigate("/teacher/lessons")}
          aria-label="Back to library"
          title="Back to library"
        >
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>
        <input
          className="lesson-ws-title-input"
          value={draft.title}
          onChange={(ev) => setDraft((d) => ({ ...d, title: ev.target.value }))}
          placeholder="Untitled lesson"
          aria-label="Lesson title"
        />
        <span
          className={`lesson-ws-mode-pill lesson-ws-mode-pill--${isAdvanced ? "advanced" : "simple"}`}
          title={isAdvanced ? "Advanced multi-file workspace" : "Simple single-file workspace"}
        >
          {isAdvanced ? <FontAwesomeIcon icon={faCubes} /> : null} {isAdvanced ? "Advanced" : "Simple"}
        </span>
        {statusBadge}
      </div>

      <div className="lesson-ws-topbar-right">
        {showEditor ? (
          <button
            type="button"
            className={`btn secondary tight${previewVisible ? " is-active" : ""}`}
            onClick={() => setPreviewVisible((v) => !v)}
            aria-pressed={previewVisible}
          >
            {previewVisible ? "Hide preview" : "Show preview"}
          </button>
        ) : null}
        <button
          type="button"
          className="btn primary"
          disabled={saving || !draft.title.trim()}
          onClick={onSaveClick}
        >
          {saveLabel}
        </button>
        {savedLessonId ? (
          <div className="lesson-ws-menu">
            <button
              ref={menuTriggerRef}
              type="button"
              className="btn secondary tight"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="More actions"
            >
              <FontAwesomeIcon icon={faEllipsisVertical} />
            </button>
            <FloatingActionsMenu
              anchorRef={menuTriggerRef}
              open={menuOpen}
              onClose={() => setMenuOpen(false)}
            >
              {fullPreviewUrl ? (
                <Link
                  to={fullPreviewUrl}
                  role="menuitem"
                  className="teacher-actions-menu-item"
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => setMenuOpen(false)}
                >
                  <FontAwesomeIcon icon={faUpRightFromSquare} className="teacher-actions-menu-icon" />
                  Open full preview
                </Link>
              ) : null}
              {!isPublished && (isDraft || canMarkAsDraft) ? (
                <button
                  type="button"
                  role="menuitem"
                  className="teacher-actions-menu-item"
                  disabled={togglingDraft}
                  onClick={() => void handleToggleDraft()}
                >
                  <FontAwesomeIcon icon={faFolderOpen} className="teacher-actions-menu-icon" />
                  {togglingDraft
                    ? "Updating…"
                    : isDraft
                      ? "Move to library"
                      : "Move to drafts"}
                </button>
              ) : null}
              <button
                type="button"
                role="menuitem"
                className="teacher-actions-menu-item teacher-actions-menu-item--danger"
                disabled={deleting}
                onClick={() => {
                  setMenuOpen(false);
                  void handleDelete();
                }}
              >
                <FontAwesomeIcon icon={faTrash} className="teacher-actions-menu-icon" />
                {deleting ? "Deleting…" : "Delete lesson"}
              </button>
            </FloatingActionsMenu>
          </div>
        ) : null}
      </div>
    </header>
  );

  const banners = (
    <>
      {error ? (
        <div className="warn-banner lesson-ws-banner" role="alert">
          {error}
          {upgradeCta ? (
            <>
              {" "}
              <Link to="/teacher/billing">Manage billing</Link>
            </>
          ) : null}
        </div>
      ) : null}
      {saveSuccess ? (
        <div className="teacher-callout-muted lesson-ws-banner" role="status">
          <p>{saveSuccess}</p>
        </div>
      ) : null}
    </>
  );

  const tabs: { id: AuthorTab; label: string; badge?: number }[] = [
    { id: "details", label: "Details" },
    { id: "code", label: "Code" },
    { id: "instructions", label: "Instructions", badge: draft.tourSteps.length },
    { id: "checks", label: "Checks", badge: draft.tests.length },
    {
      id: "standards",
      label: "Standards",
      badge: standards.selectedStandardIds.size + standards.selectedGlossaryIds.size,
    },
  ];

  const authorPane = (
    <div className="lesson-ws-author">
      <div className="lesson-ws-author-toolbar">
        <nav className="lesson-ws-tabs" aria-label="Lesson sections">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              className={`lesson-ws-tab${activeTab === t.id ? " is-active" : ""}`}
              onClick={() => setActiveTab(t.id)}
              aria-current={activeTab === t.id}
            >
              {t.label}
              {typeof t.badge === "number" ? (
                <span className="lesson-ws-tab-badge">{t.badge}</span>
              ) : null}
            </button>
          ))}
        </nav>
        <button
          type="button"
          className={`btn secondary tight lesson-ws-ai-toggle${aiOpen ? " is-active" : ""}`}
          onClick={() => setAiOpen((v) => !v)}
          aria-pressed={aiOpen}
        >
          <FontAwesomeIcon icon={faWandMagicSparkles} /> AI assist
        </button>
      </div>

      {aiOpen ? (
        <LessonAiAssistPanel
          variant="inline"
          hasContent={hasContent}
          bearer={bearer}
          mentionDoc={mentionDoc}
          onMentionDocChange={setMentionDoc}
          standardSetId={activeSetId}
          onStandardSetIdChange={setStandardSetId}
          standardSets={catalog.sets}
          aiBusy={aiBusy}
          aiProgressMode={aiProgressMode}
          aiProgress={aiProgress}
          blockedByAdvancedPrompt={advancedPrompt !== null}
          onGenerate={() => void handleGenerate()}
          onRefine={() => void handleRefine()}
        />
      ) : null}

      <div className="lesson-ws-author-body">
        {activeTab === "details" ? (
          <LessonDetailsSection
            draft={draft}
            setDraft={setDraft}
            savedLessonId={savedLessonId}
            creationPrompt={creationPrompt}
          />
        ) : null}
        {activeTab === "code" ? <LessonCodeSection draft={draft} setDraft={setDraft} /> : null}
        {activeTab === "instructions" ? (
          <LessonInstructionsSection draft={draft} setDraft={setDraft} updateStep={updateStep} />
        ) : null}
        {activeTab === "checks" ? (
          <LessonChecksSection draft={draft} setDraft={setDraft} updateTest={updateTest} />
        ) : null}
        {activeTab === "standards" ? <LessonStandardsSection selection={standards} /> : null}
      </div>
    </div>
  );

  return (
    <div className="lesson-ws">
      {topBar}
      {banners}

      {!showEditor ? (
        <div className="lesson-ws-empty">
          <LessonAiAssistPanel
            variant="empty"
            hasContent={false}
            bearer={bearer}
            mentionDoc={mentionDoc}
            onMentionDocChange={setMentionDoc}
            standardSetId={activeSetId}
            onStandardSetIdChange={setStandardSetId}
            standardSets={catalog.sets}
            aiBusy={aiBusy}
            aiProgressMode={aiProgressMode}
            aiProgress={aiProgress}
            blockedByAdvancedPrompt={advancedPrompt !== null}
            onGenerate={() => void handleGenerate()}
            onRefine={() => void handleRefine()}
            onStartBlank={() => setStartedBlank(true)}
          />
        </div>
      ) : (
        <div
          className={`lesson-ws-split${previewVisible && split.wideLayout ? " is-split" : ""}${
            split.dragging ? " is-dragging" : ""
          }`}
          ref={splitRef}
          style={
            previewVisible && split.wideLayout
              ? { gridTemplateColumns: split.gridTemplateColumns }
              : undefined
          }
        >
          {authorPane}
          {previewVisible ? (
            <>
              {split.wideLayout ? (
                <PlaygroundPanelSplitter
                  ariaLabel="Resize editor and preview"
                  active={split.dragging}
                  onPointerDown={split.onSplitterPointerDown}
                />
              ) : null}
              <LessonPreviewPane draft={draft} savedLessonId={savedLessonId} />
            </>
          ) : null}
        </div>
      )}

      <LessonAdvancedModePromptModal
        open={advancedPrompt !== null}
        lessonTitle={advancedPrompt?.pendingDraft.title ?? draft.title}
        rationale={advancedPrompt?.rationale ?? ""}
        busy={advancedPromptBusy || aiBusy}
        onAcceptAdvanced={handleAcceptAdvancedMode}
        onPreferSimple={() => void handlePreferSimpleMode()}
      />

      <WhatsChangedModal
        open={changelogOpen}
        kind="lessons"
        title={draft.title || "lesson"}
        before={savedBaseline}
        after={draft}
        saving={saving}
        onCancel={() => setChangelogOpen(false)}
        onConfirm={(changelog) => {
          setChangelogOpen(false);
          void handleSave(changelog);
        }}
      />
      <Toast />
    </div>
  );
}
